"use client";

import {
  ComponentProps,
  createContext,
  Dispatch,
  ReactNode,
  RefObject,
  SetStateAction,
  useContext,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { AlertTriangle, LucideIcon } from "lucide-react";
import {
  AppRouterInstance,
  NavigateOptions,
} from "next/dist/shared/lib/app-router-context.shared-runtime";
import NextLink from "next/link";
import { usePathname, useRouter as useNextRouter } from "next/navigation";

import { AlertDescription } from "@/components/_ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/_ui/alert-dialog";
import { Button } from "@/components/_ui/button";
import { getHrefPathname } from "@/utils/utils";

interface RequestedDestination {
  href: string;
  method: "push" | "replace";
  options?: NavigateOptions;
}

interface NavigationContextProps {
  isAppLocked: RefObject<boolean>;
  isPageLocked: RefObject<boolean>;
  requestedDestination: RequestedDestination | null;
  pathname: string;
  addAlert: (id: string, type: "app" | "page") => void;
  removeAlert: (id: string, type: "app" | "page") => void;
  setRequestedDestination: Dispatch<
    SetStateAction<RequestedDestination | null>
  >;
}

const NavigationContext = createContext<NavigationContextProps | null>(null);

interface PageNavigationAlertProps {
  icon?: LucideIcon;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

type AlertReducerAction =
  | {
      type: "add";
      data: {
        id: string;
        type: "app" | "page";
      };
    }
  | {
      type: "remove";
      data: {
        id: string;
        type: "app" | "page";
      };
    };

const alertReducer = (
  state: {
    app: Set<string>;
    page: Set<string>;
  },
  action: AlertReducerAction,
) => {
  const copy = { ...state };
  switch (action.type) {
    case "add": {
      copy[action.data.type].add(action.data.id);
      return copy;
    }
    case "remove": {
      copy[action.data.type].delete(action.data.id);
      return copy;
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};

interface NavigationProviderProps {
  children: ReactNode;
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  // Refs to track if the app or page is locked
  // Refs are used to avoid unnecessary re-renders and allow the
  // useRouter hook functions to access the current state with accuracy
  const isAppLocked = useRef(false);
  const isPageLocked = useRef(false);

  // Complex state object to track current AppNavigationAlert and PageNavigationAlert
  // components that are active in the application
  const [alertState, dispatchAlert] = useReducer(alertReducer, {
    app: new Set<string>(),
    page: new Set<string>(),
  });

  const [requestedDestination, setRequestedDestination] =
    useState<RequestedDestination | null>(null);
  const pathname = usePathname();

  // Synchronously updated mirror of alertState.page. The reducer's Set only
  // changes on the NEXT render, so two page locks unmounting in the same
  // commit each saw the other still present and neither cleanup cleared the
  // destination — the next page alert to mount then popped a confirm the user
  // never asked for. The ref is the ledger removeAlert decides "last lock
  // out" against; the reducer state stays the render-driving copy.
  const pageAlertIdsRef = useRef(new Set<string>());

  // Update the isAppLocked and isPageLocked refs whenever the alertState changes
  // This ensures that the refs always reflect the current state of the alerts
  useEffect(() => {
    isAppLocked.current = alertState.app.size > 0;
    isPageLocked.current = alertState.page.size > 0;
  }, [alertState]);

  // Prevent navigation when the app or page is locked
  // This is done by listening to the beforeunload event
  // If the app or page is locked, we prevent the default action
  // and return true to show the system alert dialog
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isAppLocked.current || isPageLocked.current) {
        event.preventDefault();
        return true;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [alertState]);

  // Reset requestedDestination when the pathname changes
  // This ensures that if the user navigates to a new page, the requested destination is cleared
  // This is important to prevent the alert dialog from showing up when navigating to a new page
  // after a previous navigation was requested but not confirmed
  useEffect(() => {
    setRequestedDestination(null);
  }, [pathname]);

  return (
    <NavigationContext.Provider
      value={{
        isAppLocked,
        isPageLocked,
        requestedDestination,
        pathname,
        addAlert: (id, type) => {
          if (type === "page") pageAlertIdsRef.current.add(id);
          dispatchAlert({ type: "add", data: { id, type } });
        },
        removeAlert: (id, type) => {
          dispatchAlert({ type: "remove", data: { id, type } });
          // A PAGE alert leaving while the confirm is open: the pending
          // question leaves with the LAST component asking it. While OTHER
          // page alerts remain mounted their lock still owns the dialog —
          // clearing unconditionally (as the alert's own unmount cleanup
          // used to) killed a surviving lock's confirm mid-decision and
          // silently dropped the user's navigation. Decided against the
          // synchronously-updated ref, never the reducer's Set (see its doc).
          if (type === "page") {
            pageAlertIdsRef.current.delete(id);
            if (pageAlertIdsRef.current.size === 0) {
              setRequestedDestination(null);
            }
          }
        },
        setRequestedDestination,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigationContext() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error(
      "useNavigationContext must be used within a NavigationProvider",
    );
  }
  return context;
}

// This component is used to show a system alert dialog when the app is locked
// i.e prevent the user from navigating away from the app without confirmation
export function AppNavigationAlert() {
  const { addAlert, removeAlert } = useNavigationContext();
  const id = useId();

  // This effect register the component with the navigation context
  // and removes it when the component is unmounted
  useEffect(() => {
    addAlert(id, "app");
    return () => removeAlert(id, "app");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
}

// This component is used to show an application alert dialog when the page is locked
// i.e prevent the user from navigating away from the current page without confirmation
export function PageNavigationAlert({
  icon = AlertTriangle,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
}: PageNavigationAlertProps) {
  const {
    requestedDestination,
    addAlert,
    removeAlert,
    setRequestedDestination,
  } = useNavigationContext();
  const id = useId();
  const router = useNextRouter();

  // This effect registers the component with the navigation context
  // and removes it when the component is unmounted
  useEffect(() => {
    addAlert(id, "page");
    return () => {
      // The lock reason can VANISH while the confirm is open (a client-bound
      // upload finishes, so the host unmounts this alert). The PROVIDER
      // clears the recorded destination when the LAST page alert leaves —
      // done here unconditionally, one surface's finished upload used to
      // kill the confirm another still-locked surface had open.
      removeAlert(id, "page");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize the icon to avoid unnecessary re-renders
  const HeaderIcon = useMemo(() => {
    return icon;
  }, [icon]);

  const handleCancel = () => {
    setRequestedDestination(null);
  };

  const handleConfirm = () => {
    if (requestedDestination) {
      if (requestedDestination.method === "replace")
        router.replace(requestedDestination.href, requestedDestination.options);
      else router.push(requestedDestination.href, requestedDestination.options);
      setRequestedDestination(null);
    }
  };

  return (
    <AlertDialog open={requestedDestination !== null}>
      <AlertDialogContent className="gap-0 space-y-0 overflow-hidden p-0">
        <AlertDialogHeader className="bg-muted/50 space-y-0 rounded-t-lg border-b p-4">
          <div className="flex items-center gap-2">
            <HeaderIcon />
            <AlertDialogTitle className="text-lg font-semibold">
              {title}
            </AlertDialogTitle>
          </div>
        </AlertDialogHeader>
        <div className="m-0! flex max-h-[calc(100vh-14rem)] flex-col gap-4 overflow-y-auto p-4">
          <AlertDescription>{message}</AlertDescription>
        </div>
        <AlertDialogFooter className="bg-muted/50 rounded-b-lg border-t p-4">
          <div className="flex w-full items-center justify-between">
            <AlertDialogCancel asChild onClick={handleCancel}>
              <Button size="sm" variant="outline">
                {cancelText}
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild onClick={handleConfirm}>
              <Button size="sm" variant="destructive">
                {confirmText}
              </Button>
            </AlertDialogAction>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Custom Link component that handles page locking
// If the page is locked, it prevents navigation and sets the requested destination
export function Link({ className, ...props }: ComponentProps<typeof NextLink>) {
  const { isPageLocked, pathname, setRequestedDestination } =
    useNavigationContext();

  const handleNavigate = (e: { preventDefault: () => void }) => {
    if (
      isPageLocked.current &&
      getHrefPathname(props.href.toString()) !== pathname
    ) {
      e.preventDefault();
      setRequestedDestination({
        href: props.href.toString(),
        method: props.replace ? "replace" : "push",
        options: {
          scroll: props.scroll,
        },
      });
    }
  };

  return (
    <NextLink className={className} onNavigate={handleNavigate} {...props} />
  );
}

export function useRouter(): {
  router: AppRouterInstance;
  navigation: Omit<
    NavigationContextProps,
    "addAlert" | "removeAlert" | "setRequestedDestination"
  >;
} {
  const router = useNextRouter();
  const {
    addAlert: _,
    removeAlert: __,
    pathname,
    setRequestedDestination,
    isPageLocked,
    ...navigation
  } = useNavigationContext();

  // Memoized: a fresh object per render turned every consumer that keyed an
  // effect or callback on `router` into a per-render re-run — the places
  // header-intent effects hit React's update-depth limit exactly this way
  // (router.replace re-renders before the URL commits, the new identity
  // re-fired the effect, which replaced again). `isPageLocked` is a ref —
  // the LOCK VALUE is read at call time, so lock flips never (and must
  // never) rebuild the router; the ref OBJECT sits in the dependency array
  // below only to satisfy exhaustive-deps, and its identity is stable for
  // the provider's lifetime.
  const guardedRouter = useMemo<AppRouterInstance>(
    () => ({
      back: router.back,
      forward: router.forward,
      prefetch: router.prefetch,
      push: (href, options) => {
        if (
          isPageLocked &&
          isPageLocked.current &&
          getHrefPathname(href.toString()) !== pathname
        )
          setRequestedDestination({ href, method: "push", options });
        else router.push(href, options);
      },
      replace: (href, options) => {
        if (
          isPageLocked &&
          isPageLocked.current &&
          getHrefPathname(href.toString()) !== pathname
        )
          setRequestedDestination({ href, method: "replace", options });
        else router.replace(href, options);
      },
      refresh: () => {
        if (isPageLocked && isPageLocked.current) {
          const currentUrl = new URL(window.location.href);
          setRequestedDestination({
            href: currentUrl.toString(),
            method: "replace",
          });
        } else router.refresh();
      },
    }),
    [router, pathname, setRequestedDestination, isPageLocked],
  );

  // Same identity discipline as the router: no consumer keys on `navigation`
  // today, but the first one that does would inherit the per-render-rerun
  // trap the router memo above exists to prevent.
  const stableNavigation = useMemo(
    () => ({ ...navigation, isPageLocked, pathname }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      navigation.isAppLocked,
      navigation.requestedDestination,
      isPageLocked,
      pathname,
    ],
  );

  return {
    router: guardedRouter,
    navigation: stableNavigation,
  };
}
